import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { FavoriteItem } from '../../types/favorites';
import { WalkIcon, BookIcon, BikeIcon, AppStoreIcon, GooglePlayIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { isEmptyField } from '../../utils/timeUtils';
import { logger } from '../../lib/logger';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime, formatTimeRange } from '../../utils/formatDuration';
import { favoritesService } from '../../lib/favorites/favoritesService';
import { analyticsService } from '../../lib/analytics/analyticsService';

interface FavoritesDetailCardProps {
  item: FavoriteItem;
  onClose: () => void;
  onRemoveFavorite: (itemId: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
}

export const FavoritesDetailCard: React.FC<FavoritesDetailCardProps> = ({
  item,
  onClose,
  onRemoveFavorite,
  config,
  translations,
  categoryColors,
  onCategorySelect,
  onTypeSelect
}) => {
  const { i18n } = useTranslation();
  const data = item.data;
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);

  // Build translation context based on section type
  const translationContext = React.useMemo(() => {
    // Use the original item ID stored in metadata
    const itemId = item.metadata.item_id || item.id;

    logger.debug('Building translation context for favorite item', {
      category: 'Translation',
      data: {
        itemId,
        itemName: item.data.name,
        sourceType: item.sourceType,
        idShow: item.metadata.idShow,
        source_hash: item.metadata.source_hash
      }
    });

    return {
      tableId: item.metadata.section_id,
      itemId, // Use the retrieved ID
      sectionType: item.sourceType,
      idShow: item.metadata.idShow,
      source_hash: item.metadata.source_hash
    };
  }, [item]);

  // Use the useAutoTranslation hook with the appropriate context
  const { translate, translating, error: translationError, hasValidTranslation } = useAutoTranslation({
    description: data.description,
    currentLanguage: i18n.language,
    context: translationContext,
    existingTranslations: item.translations?.descriptions || {}
  });

  useEffect(() => {
    const abortController = new AbortController();

    const checkAndTranslate = async () => {
      if (abortController.signal.aborted) return;

      const currentLang = i18n.language;
      
      // Check if a translation already exists in storage
      const existingTranslation = item.translations?.descriptions?.[currentLang];
      const isDescriptionMissing = !existingTranslation || 
                                 existingTranslation.trim() === '' || 
                                 existingTranslation === '\n';
      
      if (isDescriptionMissing) {
        logger.debug('Translation needed for favorite item', {
          category: 'Translation',
          data: { 
            itemId: translationContext.itemId,
            itemName: item.data.name,
            language: currentLang,
            sourceType: item.sourceType
          }
        });

        try {
          const translated = await translate();
          if (translated && !abortController.signal.aborted) {
            setTranslatedDescription(translated);
            // Update the translations of the item
            if (!item.translations) {
              item.translations = { descriptions: {} };
            }
            if (!item.translations.descriptions) {
              item.translations.descriptions = {};
            }
            item.translations.descriptions[currentLang] = translated;
            
            // Update the translation in the favorites service
            favoritesService.updateFavoriteTranslation(item.id, currentLang, translated);

            logger.debug('Translation completed for favorite item', {
              category: 'Translation',
              data: { 
                itemId: translationContext.itemId,
                itemName: item.data.name,
                language: currentLang,
                translatedLength: translated.length
              }
            });
          }
        } catch (error) {
          if (!abortController.signal.aborted) {
            logger.error('Translation failed for favorite item', {
              category: 'Translation',
              data: { 
                error, 
                itemId: translationContext.itemId,
                itemName: item.data.name,
                context: translationContext
              }
            });
          }
        }
      } else {
        if (!abortController.signal.aborted) {
          setTranslatedDescription(existingTranslation);
          logger.debug('Using existing translation for favorite item', {
            category: 'Translation',
            data: {
              itemId: translationContext.itemId,
              itemName: item.data.name,
              language: currentLang
            }
          });
        }
      }
    };

    checkAndTranslate();

    return () => {
      abortController.abort();
    };
  }, [item.translations?.descriptions, i18n.language, translate, translationContext, item.data.name]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const renderContent = () => {
    // Determine which content to render based on source type
    switch (item.sourceType) {
      case 'event':
      case 'event-api':
        return renderEventContent();
      case 'standard':
        return renderStandardContent();
      case 'liste':
        return renderListContent();
      default:
        return null;
    }
  };

  const renderEventContent = () => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      {/* Place */}
      <div className="w-full">
        {data.place}
      </div>
      
      {/* Address + telephone */}
      <div className="w-full">
        {data.fulladress && <span>{data.fulladress}</span>}
        {data.fulladress && data.telephone && <span> • </span>}
        {data.telephone && <span>{formatPhoneNumber(data.telephone)}</span>}
      </div>
      
      {/* Distance + travel times */}
      <div className="flex flex-wrap gap-2">
        {!isEmptyField(data.distance) && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{data.distance}km</span>
          </div>
        )}
        
        {!isEmptyField(data.timewalk) && (
          <div className="flex items-center">
            <WalkIcon size={15} className="mr-1" />
            <span>{formatTravelTime(data.timewalk, i18n.language)}</span>
          </div>
        )}
        
        {!isEmptyField(data.timebike) && (
          <div className="flex items-center">
            <BikeIcon size={15} className="mr-1" />
            <span>{formatTravelTime(data.timebike, i18n.language)}</span>
          </div>
        )}
        
        {!isEmptyField(data.timedrive) && (
          <div className="flex items-center">
            <Car size={15} className="mr-1" fill="currentColor" />
            <span>{formatTravelTime(data.timedrive, i18n.language)}</span>
          </div>
        )}
      </div>
      
      {/* Date, time, price */}
      <div className="flex flex-wrap gap-2 items-center">
        {data.date && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              }).format(new Date(data.date))}
              {data.hour && data.end_time ? (
                ` ${formatTimeRange(data.hour, data.end_time, i18n.language)}`
              ) : data.hour && data.duration ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${data.hour}`))} (${formatTravelTime(data.duration, i18n.language)})`
              ) : data.hour ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${data.hour}`))}`
              ) : ''}
            </span>
          </div>
        )}
        
        {data.price && (
          <div 
            className="flex items-center"
            style={{ color: config['major-color'] }}
          >
            <Tag size={15} className="mr-1" />
            <span>{data.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderStandardContent = () => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      {/* Address + telephone */}
      <div className="w-full">
        {data.fulladress && <span>{data.fulladress}</span>}
        {data.fulladress && data.telephone && <span> • </span>}
        {data.telephone && <span>{formatPhoneNumber(data.telephone)}</span>}
      </div>
      
      {/* Distance + travel times */}
      <div className="flex flex-wrap gap-2">
        {data.inHotel ? (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{getTranslatedText('in_hotel')}</span>
          </div>
        ) : (
          <>
            {!isEmptyField(data.distance) && (
              <div className="flex items-center">
                <MapPin size={15} className="mr-1" />
                <span>{data.distance}km</span>
              </div>
            )}
            
            {!isEmptyField(data.timewalk) && (
              <div className="flex items-center">
                <WalkIcon size={15} className="mr-1" />
                <span>{formatTravelTime(data.timewalk, i18n.language)}</span>
              </div>
            )}
            
            {!isEmptyField(data.timebike) && (
              <div className="flex items-center">
                <BikeIcon size={15} className="mr-1" />
                <span>{formatTravelTime(data.timebike, i18n.language)}</span>
              </div>
            )}
            
            {!isEmptyField(data.timedrive) && (
              <div className="flex items-center">
                <Car size={15} className="mr-1" fill="currentColor" />
                <span>{formatTravelTime(data.timedrive, i18n.language)}</span>
              </div>
            )}
          </>
        )}
        
        {/* Monday opening */}
        {data.ouverture_lundi === 'oui' && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>{getTranslatedText('events_monday_open')}</span>
          </div>
        )}
        
        {/* Price */}
        {data.price && (
          <div 
            className="flex items-center"
            style={{ color: config['major-color'] }}
          >
            <Tag size={15} className="mr-1" />
            <span>{data.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderListContent = () => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      {/* Address + telephone */}
      <div className="w-full">
        {data.fulladress && <span>{data.fulladress}</span>}
        {data.fulladress && data.telephone && <span> • </span>}
        {data.telephone && <span>{formatPhoneNumber(data.telephone)}</span>}
      </div>
    </div>
  );

  const renderBookingButton = () => {
    // For list items, display App Store and Play Store buttons
    if (item.sourceType === 'liste') {
      const listData = data as any;
      
      // Check if at least one store is available
      if (!listData.appstore && !listData.playstore) {
        return null;
      }

      return (
        <div className="flex flex-col gap-2 mb-4">
          {listData.appstore && (
            <a
              href={listData.appstore}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: config['major-color'] }}
              onClick={() => {
                analyticsService.trackItemAction(
                  'favorites',
                  item.id,
                  'app_store_click',
                  'favorite',
                  {
                    item_name: item.data.name,
                    url: listData.appstore,
                    section_name: 'Favorites',
                    section_type: 'favorites'
                  }
                );
              }}
            >
              <AppStoreIcon size={18} className="mr-2" />
              {getTranslatedText('btn_app_store')}
            </a>
          )}
          {listData.playstore && (
            <a
              href={listData.playstore}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: config['major-color'] }}
              onClick={() => {
                analyticsService.trackItemAction(
                  'favorites',
                  item.id,
                  'play_store_click',
                  'favorite',
                  {
                    item_name: item.data.name,
                    url: listData.playstore,
                    section_name: 'Favorites',
                    section_type: 'favorites'
                  }
                );
              }}
            >
              <GooglePlayIcon size={18} className="mr-2" />
              {getTranslatedText('btn_play_store')}
            </a>
          )}
        </div>
      );
    }

    // For other types, display the standard booking button
    if (!data.booking) return null;
    
    return (
      <a
        href={data.booking}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-4"
        style={{ backgroundColor: config['major-color'] }}
        onClick={() => {
          analyticsService.trackItemAction(
            'favorites',
            item.id,
            'booking_click',
            'favorite',
            {
              item_name: item.data.name,
              url: data.booking,
              section_name: 'Favorites',
              section_type: 'favorites'
            }
          );
        }}
      >
        <BookIcon size={18} className="mr-2" />
        {getTranslatedText('btn_booking')}
      </a>
    );
  };

  // Build itinerary URL from hotel
  const getHotelItineraryUrl = (): string => {
    if (!data.fulladress || !config.fulladress) return '';

    // Encode addresses for URL
    const origin = encodeURIComponent(config.fulladress);
    const destination = encodeURIComponent(data.fulladress);

    // Build URL with hotel coordinates as origin
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  };

  // Get description to display
  const getDescription = () => {
    const currentLang = i18n.language;
    
    if (translating) {
      return data.description;
    }
    
    if (translatedDescription) {
      return translatedDescription;
    }
    
    return item.translations?.descriptions?.[currentLang] || data.description;
  };

  // Use the item's image and only take replacement image if necessary
  const displayImage = data.image || item.metadata.sourceSection.replace_img?.[0]?.url;

  return (
    <BaseDetailCard
      item={data}
      items={[data]}
      onClose={onClose}
      onItemChange={() => {}}
      config={config}
      translations={translations}
      tableId={item.metadata.section_id}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={getTranslatedCategory}
      getTranslatedText={getTranslatedText}
      itineraryUrl={getHotelItineraryUrl()}
      context={{ 
        sectionType: item.sourceType,
        itemId: item.metadata.item_id,
        idShow: item.metadata.idShow,
        source_hash: item.metadata.source_hash
      }}
      isFavoriteCard={true}
      image={displayImage}
      description={getDescription()}
      translating={translating}
      translationError={translationError}
      onRemoveFavorite={onRemoveFavorite}
    />
  );
};

export default FavoritesDetailCard;